import useSWR, { preload } from 'swr';
import { useCallback } from 'react';
import { UserProfile } from 'src/types/common.types';
import { UserAccountState } from 'src/enums/shared.enums';
import { useAuth } from '../auth/useAuth';
import { getProfileApi } from 'src/api';

const buildProfile = (raw: any): UserProfile => ({
  userId: raw.userId || '',
  email: raw.email || '',
  state: raw.state || UserAccountState.REGISTERED,
  accountType: raw.accountType || 'artist',
  displayName: raw.displayName || (raw.email ? raw.email.split('@')[0] : ''),
  firstName: raw.firstName || '',
  lastName: raw.lastName || '',
  bio: raw.bio || '',
  createdAt: raw.createdAt || new Date().toISOString(),
});

const fetcher = async () => {
  const response = await getProfileApi();
  return response?.profile ? buildProfile(response.profile) : null;
};

export const preloadProfile = () => preload(['profile'], fetcher);

export const useProfile = () => {
  const { isAuthenticated, profile: authProfile } = useAuth();

  const { data, error, isLoading, mutate } = useSWR<UserProfile | null>(
    isAuthenticated ? ['profile'] : null,
    fetcher
  );

  // Use SWR data, fallback to auth context while loading
  const profile = data ?? (isAuthenticated && authProfile ? buildProfile(authProfile) : null);

  const refreshProfile = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const updateProfileCache = useCallback(async (updated: UserProfile) => {
    await mutate(updated, false);
  }, [mutate]);

  const clearProfileCache = useCallback(async () => {
    await mutate(null, false);
  }, [mutate]);

  return {
    profile,
    // Report loading only when SWR is fetching AND we have no usable data
    // (not even fallback from auth context). This prevents AuthGuard from
    // blocking when profile data is already available via the auth context.
    loading: isLoading && !profile,
    error,
    refreshProfile,
    updateProfileCache,
    clearProfileCache,
  };
};
